import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
} from '@tanstack/react-table';
import { Button } from './ui.js';
import {
  DEFAULT_ROWS,
  ROWS_OPTIONS,
  pageView,
  parseRowsSetting,
  rangeLabel,
  type RowsSetting,
} from '../lib/table-paging.js';

/**
 * A sortable table over rows already in memory, for the SKU and restock lists.
 *
 * Sorting and the rows-to-display slice run over the whole dataset the caller
 * hands in; the caller filters and searches before passing it. Every sortable
 * column carries an explicit `sortingFn` (see lib/table-sort.ts) and, for a
 * nullable column, `sortUndefined: 'last'` with an accessor that maps null to
 * undefined, so nulls stay last in both directions.
 *
 * The top bar carries the caller's own filters (`toolbar`) beside a rows
 * selector (50 / 100 / All) and, for a fixed size, a compact pager; the choice
 * is remembered per table in localStorage. When the table is wider than its
 * card the horizontal scrollbar is moved above the header, made sticky so it
 * stays in view while a long list scrolls, and synced with the table; the first
 * column can be pinned to the left. The header row is NOT pinned to the top:
 * the horizontal-scroll container makes `overflow-y` compute to `auto`, so a
 * sticky header would bind to that container (which never scrolls vertically)
 * rather than the page.
 */

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    numeric?: boolean;
  }
}

function readRows(storageKey: string | undefined): RowsSetting {
  if (!storageKey) return DEFAULT_ROWS;
  try {
    return parseRowsSetting(localStorage.getItem(`seller.rows.${storageKey}`));
  } catch {
    return DEFAULT_ROWS;
  }
}

export function DataTable<T>({
  data,
  columns,
  initialSorting = [],
  onRowClick,
  emptyText = 'Nothing to show.',
  toolbar,
  storageKey,
  resetKey,
  dense = false,
  stickyFirstColumn = false,
  getRowId,
}: {
  data: T[];
  // TanStack's own signature: a column's value type varies per column, so
  // the array is typed over `any` by the library and by every caller of it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  initialSorting?: SortingState;
  onRowClick?: (row: T) => void;
  emptyText?: ReactNode;
  /** The caller's own filters, shown at the left of the top bar. */
  toolbar?: ReactNode;
  /** Names the localStorage slot for the remembered rows-per-page choice. */
  storageKey?: string;
  /**
   * A stable row identity (e.g. the SKU id). Without it TanStack keys rows by
   * index, so an inline editor can carry a half-typed value onto a different row
   * after a sort or filter change; with it, each row keeps its own editor state.
   */
  getRowId?: (row: T) => string;
  /**
   * Changes to this value (the caller's filter/search state) return the table
   * to page 1. A plain data refetch that leaves the filters alone does not, so
   * an equal refetch keeps the reader where they were.
   */
  resetKey?: string;
  /** Tighter horizontal cell padding, for a table with many columns. */
  dense?: boolean;
  /** Pin the first column to the left while the rest scrolls horizontally. */
  stickyFirstColumn?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [rows, setRows] = useState<RowsSetting>(() => readRows(storageKey));
  const [page, setPage] = useState(1);

  // Changing the sort returns to page 1 with the sort itself, so there is no
  // extra render where the reader sees the old page against the new order.
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    setPage(1);
  };

  function changeRows(next: RowsSetting) {
    setRows(next);
    setPage(1);
    if (storageKey) {
      try {
        localStorage.setItem(`seller.rows.${storageKey}`, String(next));
      } catch {
        // The preference simply will not persist; the session still honors it.
      }
    }
  }

  // A filter or search change (a new resetKey) returns to page 1 during render,
  // so there is no one-frame flash of the old page. An equal refetch leaves the
  // resetKey alone and the reader stays put.
  const lastResetKey = useRef(resetKey);
  if (lastResetKey.current !== resetKey) {
    lastResetKey.current = resetKey;
    if (page !== 1) setPage(1);
  }

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    defaultColumn: { sortUndefined: 'last' },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  const sortedRows = table.getRowModel().rows;
  // pageView clamps the page, so a page stranded past the end after a filter
  // shows the last page rather than nothing — no effect, no flash.
  const view = pageView(sortedRows.length, rows, page);
  const visibleRows = sortedRows.slice(view.start, view.end);

  // Horizontal overflow: the scrollbar, moved to the top, and the pinned-column
  // shadow both depend on how wide the table is against its container.
  const bodyRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      setScrollWidth(el.scrollWidth);
      setClientWidth(el.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const tableEl = el.querySelector('table');
    if (tableEl) observer.observe(tableEl);
    return () => observer.disconnect();
  }, []);

  const overflowing = scrollWidth > clientWidth + 1;

  function syncFromTop() {
    if (topRef.current && bodyRef.current) {
      bodyRef.current.scrollLeft = topRef.current.scrollLeft;
    }
  }
  function syncFromBody() {
    if (topRef.current && bodyRef.current) {
      topRef.current.scrollLeft = bodyRef.current.scrollLeft;
      setScrolled(bodyRef.current.scrollLeft > 0);
    }
  }

  const padX = dense ? 'px-2' : 'px-3';
  const pinnedShadow =
    stickyFirstColumn && scrolled ? 'shadow-[6px_0_6px_-4px_rgba(0,0,0,0.18)]' : '';
  const stickyHead = stickyFirstColumn ? `sticky left-0 z-20 bg-surface ${pinnedShadow}` : '';
  const stickyCell = stickyFirstColumn
    ? `sticky left-0 z-10 bg-surface ${onRowClick ? 'group-hover:bg-sunken' : ''} ${pinnedShadow}`
    : '';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2 text-xs text-muted">
        {toolbar}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5">
            <span>Rows</span>
            <select
              value={String(rows)}
              onChange={(e) => changeRows(parseRowsSetting(e.target.value))}
              aria-label="Rows to display"
              className="rounded-md border-0 bg-surface py-1 pl-2 pr-7 text-xs text-strong ring-1 ring-inset ring-line-strong focus:ring-2 focus:ring-inset focus:ring-brand-600"
            >
              {ROWS_OPTIONS.map((option) => (
                <option key={String(option)} value={String(option)}>
                  {option === 'all' ? 'All' : option}
                </option>
              ))}
            </select>
          </label>
          {view.showPager && (
            <span className="flex items-center gap-2">
              <Button
                variant="ghost"
                disabled={view.page <= 1}
                onClick={() => setPage(view.page - 1)}
              >
                Previous
              </Button>
              <span>
                Page {view.page} of {view.pageCount}
              </span>
              <Button
                variant="ghost"
                disabled={view.page >= view.pageCount}
                onClick={() => setPage(view.page + 1)}
              >
                Next
              </Button>
              <span className="hidden sm:inline">{rangeLabel(view)}</span>
            </span>
          )}
        </div>
      </div>

      {overflowing && (
        <div
          ref={topRef}
          onScroll={syncFromTop}
          role="presentation"
          tabIndex={-1}
          className="top-scroll sticky top-0 z-10 overflow-x-auto overflow-y-hidden border-b border-line bg-surface"
          style={{ height: 15 }}
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}

      <div ref={bodyRef} onScroll={syncFromBody} className="no-scrollbar overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header, index) => {
                  const numeric = header.column.columnDef.meta?.numeric ?? false;
                  const sorted = header.column.getIsSorted();
                  const first = index === 0 ? stickyHead : '';
                  return (
                    <th
                      key={header.id}
                      className={`whitespace-nowrap ${padX} py-2 font-medium ${numeric ? 'text-right' : ''} ${first}`}
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-body"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span aria-hidden className="w-2 text-faint">
                            {sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : ''}
                          </span>
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-line">
            {visibleRows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={`group ${onRowClick ? 'cursor-pointer hover:bg-sunken' : ''}`}
              >
                {row.getVisibleCells().map((cell, index) => {
                  const numeric = cell.column.columnDef.meta?.numeric ?? false;
                  const first = index === 0 ? stickyCell : '';
                  return (
                    <td
                      key={cell.id}
                      className={`${padX} py-1.5 ${numeric ? 'tabular whitespace-nowrap text-right' : ''} ${first}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-sm text-muted">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
